import "@uploadcare/react-uploader/core.css";
import { useEffect, useState } from "react";
import { ListingCard } from ".";
import { SingleCheckBox } from "../../../components/Checkbox";
import { Chevron } from "../../../components/Chevron";
import { Dropdown } from "../../../components/Dropdown";
import { Flex } from "../../../components/Flex";
import { H4 } from "../../../components/Heading";
import { Radio } from "../../../components/RadioButton";
import { Switch } from "../../../components/Switch";
import { Underline } from "../../../components/Tabs";
import { Text } from "../../../components/Text";
import { Tooltip } from "../../../components/Tooltip";
import { View } from "../../../components/View";
import { MOBILE_BREAKPOINT } from "../../../config";
import {
  ApprovalSettingsInput,
  ApprovalType,
  CreatorTier,
} from "../../../graphql/generated";
import { useTheme } from "../../../hooks/useTheme";
import styled, { css } from "../../../styles";
import { lightTheme } from "../../../styles/theme";

interface Props {
  hasTikTokOffers: boolean;
  setApprovalSettings: (newApprovalSettings: ApprovalSettingsInput) => void;
  approvalSettings: ApprovalSettingsInput;
  // autoApprovalLimit: AutoApprovalLimit | null;
  // setAutoApprovalLimit: React.Dispatch<
  //   React.SetStateAction<AutoApprovalLimit | null>
  // >;
  internalBookingNotes: string;
  setInternalBookingNotes: React.Dispatch<React.SetStateAction<string>>;
  approvalSettingsError?: string;
}

// there's is some HACKY LOGIC here to determine the UI state so we can test this auto approval top creators option for manual approvals
// the UI needs to show it still as manual but it's actually a smart approval setting
// sorry future self :)

// this is used to determine the differentiator between Manual and Top Creators enabled vs actual Smart Approval settings
const DEFAULT_MIN_FOLLOWERS = 1500;

const engagementOptions = [
  { label: "1%", value: 1 },
  { label: "3% (good)", value: 3 },
  { label: "5% (high)", value: 5 },
];

const audienceOptions = [
  { label: "10%", value: 10 },
  { label: "20%", value: 20 },
  {
    label: "25%",
    value: 25,
  },
  {
    label: "30%",
    value: 30,
  },
];

const followerOptions = [
  { label: "2,000", value: 2000 },
  { label: "3,000", value: 3000 },
  { label: "5,000", value: 5000 },
  { label: "7,500", value: 7500 },
  { label: "10,000", value: 10000 },
  { label: "15,000", value: 15000 },
  { label: "20,000", value: 20000 },
  { label: "25,000", value: 25000 },
  { label: "30,000", value: 30000 },
  { label: "40,000", value: 40000 },
  { label: "50,000", value: 50000 },
  { label: "75,000", value: 75000 },
  { label: "100,000", value: 100000 },
];

const minViewsPerPostOptions = [
  { label: "1,000", value: 1000 },
  { label: "2,000", value: 2000 },
  { label: "3,000", value: 3000 },
  { label: "5,000", value: 5000 },
  { label: "7,500", value: 7500 },
  { label: "10,000", value: 10000 },
  { label: "15,000", value: 15000 },
  { label: "20,000", value: 20000 },
  { label: "25,000", value: 25000 },
  { label: "30,000", value: 30000 },
  { label: "40,000", value: 40000 },
  { label: "50,000", value: 50000 },
  { label: "75,000", value: 75000 },
  { label: "100,000", value: 100000 },
];

const ApprovalSettings = (props: Props) => {
  const isActualManual =
    props.approvalSettings.type === ApprovalType.ApprovalTypeManual;
  const isFakeManualTopCreators =
    props.approvalSettings.type === ApprovalType.ApprovalTypeAutoWithCriteria &&
    props.approvalSettings.tier === CreatorTier.CreatorTierPremium &&
    props.approvalSettings.minFollowers !== DEFAULT_MIN_FOLLOWERS; // Differentiator
  const isActualSmartTopCreators =
    props.approvalSettings.type === ApprovalType.ApprovalTypeAutoWithCriteria &&
    props.approvalSettings.tier === CreatorTier.CreatorTierPremium &&
    props.approvalSettings.minFollowers === DEFAULT_MIN_FOLLOWERS; // Differentiator
  const isActualSmartCustom =
    props.approvalSettings.type === ApprovalType.ApprovalTypeAutoWithCriteria &&
    !props.approvalSettings.tier;

  const showAsManual = isActualManual || isFakeManualTopCreators;
  const showAsSmart = isActualSmartTopCreators || isActualSmartCustom;

  let selectedApprovalTypeUI = props.approvalSettings.type;
  if (showAsManual) {
    selectedApprovalTypeUI = ApprovalType.ApprovalTypeManual;
  } else if (showAsSmart) {
    selectedApprovalTypeUI = ApprovalType.ApprovalTypeAutoWithCriteria;
  }

  return (
    <ListingCard>
      <H4 margin="0 0 m">Approval Settings</H4>
      <Underline selected={true} />
      <View margin="l 0 m">
        <Text weight="bold" margin="0">
          Approval Type
        </Text>
        <Text colorPreset="secondary" size="s" margin="0 0 l" isCompact>
          Choose how you'd like to approve influencer applications
        </Text>
      </View>
      <ApprovalTypeGrid>
        <ApprovalTypeSelect
          approvalType={ApprovalType.ApprovalTypeManual}
          selectedApprovalType={selectedApprovalTypeUI}
          onClick={() => {
            // Set to True Manual / Top Creators OFF
            props.setApprovalSettings({
              type: ApprovalType.ApprovalTypeAutoWithCriteria,
              tier: CreatorTier.CreatorTierPremium,
              minFollowers: undefined,
              minEngagementRate: undefined,
              minUkAudience: undefined,
              minAvViews: undefined,
            });
          }}
        />
        <ApprovalTypeSelect
          approvalType={ApprovalType.ApprovalTypeAutoWithCriteria}
          selectedApprovalType={selectedApprovalTypeUI}
          onClick={() => {
            // Set to Smart / Joli Top Creators ON (with differentiator)
            props.setApprovalSettings({
              type: ApprovalType.ApprovalTypeAutoWithCriteria,
              tier: CreatorTier.CreatorTierPremium,
              minFollowers: DEFAULT_MIN_FOLLOWERS, // Differentiator
              minEngagementRate: undefined,
              minUkAudience: undefined,
              minAvViews: undefined,
            });
          }}
        />

        <ApprovalTypeSelect
          approvalType={ApprovalType.ApprovalTypeAuto}
          selectedApprovalType={selectedApprovalTypeUI}
          onClick={() => {
            // Set to Approve All
            props.setApprovalSettings({
              type: ApprovalType.ApprovalTypeAuto,
              tier: undefined,
              minFollowers: undefined,
              minEngagementRate: undefined,
              minUkAudience: undefined,
              minAvViews: undefined,
            });
          }}
        />
      </ApprovalTypeGrid>

      {showAsManual || showAsSmart ? (
        <View margin="l 0 0">
          {showAsSmart ? (
            <SmartApprovalSettings
              approvalSettings={props.approvalSettings}
              setApprovalSettings={props.setApprovalSettings}
              hasTikTokOffers={props.hasTikTokOffers}
              approvalSettingsError={props.approvalSettingsError}
            />
          ) : null}

          {showAsManual ? (
            <TopCreatorsWrap>
              <Flex
                direction="row"
                align="center"
                margin="0 0 0 0"
                style={{ userSelect: "none", cursor: "pointer" }}
                onClick={() => {
                  // Toggle Top Creators state
                  if (isFakeManualTopCreators) {
                    // Currently ON (fake manual), turn OFF -> True Manual
                    props.setApprovalSettings({
                      type: ApprovalType.ApprovalTypeManual,
                      tier: undefined,
                      minFollowers: undefined,
                      minEngagementRate: undefined,
                      minUkAudience: undefined,
                      minAvViews: undefined,
                    });
                  } else {
                    // Currently OFF (true manual), turn ON -> Fake Manual
                    props.setApprovalSettings({
                      type: ApprovalType.ApprovalTypeAutoWithCriteria,
                      tier: CreatorTier.CreatorTierPremium,
                      minFollowers: undefined, // No differentiator here
                      minEngagementRate: undefined,
                      minUkAudience: undefined,
                      minAvViews: undefined,
                    });
                  }
                }}
              >
                <View style={{ flex: 1 }}>
                  <Flex direction="row" align="center" justify="space-between">
                    <View margin="0 0 0 0">
                      <Flex>
                        <Text weight="bold" margin="0 s 0 0 ">
                          Auto-Approve Top Creators
                        </Text>
                      </Flex>
                      <Text size="s" colorPreset="secondary" margin="0 xl 0 0">
                        Top-performing creators are auto-approved by default,
                        making it easier for them to book in with you. Turn this
                        off if you'd prefer to review them manually.
                      </Text>
                    </View>
                    <View>
                      <Switch
                        value={isFakeManualTopCreators} // Use the calculated flag for checked state
                        onChange={() => {
                          // onClick handles the logic, empty onChange needed for Switch
                        }}
                      />
                    </View>
                  </Flex>
                </View>
              </Flex>
            </TopCreatorsWrap>
          ) : null}
        </View>
      ) : null}
    </ListingCard>
  );
};

export default ApprovalSettings;

const SmartApprovalSettings = (props: {
  approvalSettings: ApprovalSettingsInput;
  setApprovalSettings: (newApprovalSettings: ApprovalSettingsInput) => void;
  hasTikTokOffers: boolean;
  approvalSettingsError?: string;
}) => {
  const theme = useTheme();
  const { approvalSettings, setApprovalSettings } = props;

  const [useCustomCriteria, setUseCustomCriteria] = useState(
    !approvalSettings.tier
  );

  // Initialize local state for custom criteria checkboxes
  const [enabledFollowers, setEnabledFollowers] = useState(
    useCustomCriteria && !!approvalSettings.minFollowers
  );
  const [enabledEngagement, setEnabledEngagement] = useState(
    useCustomCriteria && !!approvalSettings.minEngagementRate
  );
  const [enabledAudience, setEnabledAudience] = useState(
    useCustomCriteria && !!approvalSettings.minUkAudience
  );
  const [minAvViewsEnabled, setMinAvViewsEnabled] = useState(
    useCustomCriteria && !!approvalSettings.minAvViews
  );

  const [followersCriteria, setFollowersCriteria] = useState(
    approvalSettings.minFollowers && !approvalSettings.minFollowers
      ? approvalSettings.minFollowers
      : followerOptions[3].value
  );
  const [engagementCriteria, setEngagementCriteria] = useState(
    approvalSettings.minEngagementRate || engagementOptions[1].value
  );
  const [audienceCriteria, setAudienceCriteria] = useState(
    approvalSettings.minUkAudience || audienceOptions[1].value
  );
  const [viewsPerPostCriteria, setViewsPerPostCriteria] = useState(
    approvalSettings.minAvViews || minViewsPerPostOptions[3].value
  );

  useEffect(() => {
    let newSettings: ApprovalSettingsInput;

    if (!useCustomCriteria) {
      newSettings = {
        type:
          approvalSettings.type || ApprovalType.ApprovalTypeAutoWithCriteria,
        tier: CreatorTier.CreatorTierPremium,
        minFollowers: DEFAULT_MIN_FOLLOWERS,
        minEngagementRate: undefined,
        minUkAudience: undefined,
        minAvViews: undefined,
      };
    } else {
      newSettings = {
        type:
          approvalSettings.type || ApprovalType.ApprovalTypeAutoWithCriteria,
        tier: undefined,
        minFollowers: enabledFollowers ? Number(followersCriteria) : undefined,
        minEngagementRate: enabledEngagement ? engagementCriteria : undefined,
        minUkAudience: enabledAudience ? audienceCriteria : undefined,
        minAvViews: minAvViewsEnabled ? viewsPerPostCriteria : undefined,
      };
    }

    if (JSON.stringify(newSettings) !== JSON.stringify(approvalSettings)) {
      setApprovalSettings(newSettings);
    }
  }, [
    useCustomCriteria,
    enabledFollowers,
    enabledEngagement,
    enabledAudience,
    minAvViewsEnabled,
    followersCriteria,
    engagementCriteria,
    audienceCriteria,
    viewsPerPostCriteria,
    approvalSettings,
    setApprovalSettings,
  ]);

  const handleSelectTopCreators = () => {
    setUseCustomCriteria(false);
    setEnabledFollowers(false);
    setEnabledEngagement(false);
    setEnabledAudience(false);
    setMinAvViewsEnabled(false);
  };

  const handleSelectCustomCriteria = () => {
    setUseCustomCriteria(true);

    if (!followersCriteria) setFollowersCriteria(followerOptions[3].value);
    if (!engagementCriteria) setEngagementCriteria(engagementOptions[1].value);
    if (!audienceCriteria) setAudienceCriteria(audienceOptions[1].value);
    if (!viewsPerPostCriteria)
      setViewsPerPostCriteria(minViewsPerPostOptions[3].value);
  };

  const ensureCustomMode = () => {
    if (!useCustomCriteria) {
      handleSelectCustomCriteria();
    }
  };

  return (
    <View
      style={{
        background: theme.color.card.callout,
        borderRadius: theme.misc.borderRadius,
      }}
      padding="l"
    >
      <Text weight="bold" margin="0 0 m">
        Smart Approval Settings
      </Text>
      <Flex
        direction="row"
        margin="0 0 m 0"
        justify="space-between"
        onClick={handleSelectTopCreators}
        style={{ cursor: "pointer" }}
      >
        <Flex direction="row" margin="0 s 0 0">
          <Flex margin="xs 0 0 0">
            <Radio width={10} height={10} active={!useCustomCriteria} />
          </Flex>
          <View margin="0 0 0 m">
            <Text margin="0" weight="bold" size="s">
              Use Joli Top Creators
            </Text>
            <Text margin="0" size="xs" colorPreset="secondary">
              Automatically approve creators from our hand-picked list of top
              performers (updated regularly)
            </Text>
          </View>
        </Flex>
      </Flex>
      <Flex
        direction="row"
        justify="space-between"
        onClick={handleSelectCustomCriteria}
        style={{ cursor: "pointer" }}
      >
        <Flex direction="row" margin="0 s 0 0">
          <Flex margin="xs 0 0 0">
            <Radio width={10} height={10} active={useCustomCriteria} />
          </Flex>
          <View margin="0 0 0 m">
            <Text margin="0" weight="bold" size="s">
              Use Custom Criteria
            </Text>
            <Text margin="0" size="xs" colorPreset="secondary">
              Set your own rules for automatic approval{" "}
              <Chevron
                width={9}
                height={9}
                direction={useCustomCriteria ? "down" : undefined}
                animateTransition
              />
            </Text>
          </View>
        </Flex>
      </Flex>
      {useCustomCriteria ? (
        <CustomCriteriaWrap>
          <Flex
            direction="row"
            align="center"
            margin="m 0"
            style={{ cursor: "pointer" }}
            onClick={() => {
              ensureCustomMode();
              setEnabledFollowers(!enabledFollowers);
            }}
          >
            <SingleCheckBox size="s" checked={enabledFollowers} />{" "}
            <View
              margin="0 0 0 l"
              style={{
                flex: 1,
                opacity: enabledFollowers ? 1 : 0.7,
                marginBottom: -2,
              }}
            >
              <Flex align="center" direction="row">
                <Text margin="0" size="s" style={{ userSelect: "none" }}>
                  More than
                </Text>
                <View margin="0 l 0">
                  <Dropdown
                    width={120}
                    size="xs"
                    selectionMode="single"
                    renderLabel={() => {
                      const selectedOption = followerOptions.find(
                        (option) => option.value === Number(followersCriteria)
                      );
                      return selectedOption
                        ? selectedOption?.value.toLocaleString()
                        : followersCriteria.toLocaleString();
                    }}
                    selectedOptions={[followersCriteria.toString()]}
                    setSelectedOptions={(value) => {
                      if (value.length > 0) {
                        ensureCustomMode();
                        setFollowersCriteria(Number(value[0]));
                        if (!enabledFollowers) setEnabledFollowers(true);
                      }
                    }}
                    onDropdownClick={() => {
                      ensureCustomMode();
                      if (!enabledFollowers) setEnabledFollowers(true);
                    }}
                    disableOptionSort
                    options={followerOptions
                      .sort((a, b) => a.value - b.value)
                      .map((option) => ({
                        label: option.label,
                        value: option.value.toString(),
                      }))}
                  />
                </View>
                <Text margin="0" size="s" style={{ userSelect: "none" }}>
                  followers
                </Text>
              </Flex>
            </View>
          </Flex>
          <Flex
            direction="row"
            align="center"
            margin="m 0"
            style={{ cursor: "pointer" }}
            onClick={() => {
              ensureCustomMode();
              setMinAvViewsEnabled(!minAvViewsEnabled);
            }}
          >
            <SingleCheckBox size="s" checked={minAvViewsEnabled} />{" "}
            <View
              margin="0 0 0 l"
              style={{
                flex: 1,
                opacity: minAvViewsEnabled ? 1 : 0.7,
                marginBottom: -2,
              }}
            >
              <Flex align="center" direction="row">
                <Text margin="0" size="s" style={{ userSelect: "none" }}>
                  At least
                </Text>
                <View margin="0 l 0">
                  <Dropdown
                    width={120}
                    size="xs"
                    selectionMode="single"
                    renderLabel={() => {
                      const selectedOption = minViewsPerPostOptions.find(
                        (option) =>
                          option.value === Number(viewsPerPostCriteria)
                      );
                      return selectedOption
                        ? selectedOption?.value.toLocaleString()
                        : viewsPerPostCriteria.toLocaleString();
                    }}
                    selectedOptions={[viewsPerPostCriteria.toString()]}
                    setSelectedOptions={(value) => {
                      if (value.length > 0) {
                        ensureCustomMode();
                        setViewsPerPostCriteria(Number(value[0]));
                        if (!minAvViewsEnabled) setMinAvViewsEnabled(true);
                      }
                    }}
                    onDropdownClick={() => {
                      ensureCustomMode();
                      if (!minAvViewsEnabled) setMinAvViewsEnabled(true);
                    }}
                    disableOptionSort
                    options={minViewsPerPostOptions
                      .sort((a, b) => a.value - b.value)
                      .map((option) => ({
                        label: option.label,
                        value: option.value.toString(),
                      }))}
                  />
                </View>
                <Flex>
                  <Text
                    margin="0 s 0 0"
                    size="s"
                    style={{ userSelect: "none" }}
                  >
                    views per post
                  </Text>
                  <Tooltip>
                    <Text size="s" margin="0">
                      Average views per post calculated over the last 28 days
                    </Text>
                  </Tooltip>
                </Flex>
              </Flex>
            </View>
          </Flex>
          <Flex
            direction="row"
            align="center"
            margin="0 0 0 0"
            style={{ cursor: "pointer" }}
            onClick={() => {
              ensureCustomMode();
              setEnabledEngagement(!enabledEngagement);
            }}
          >
            <SingleCheckBox size="s" checked={enabledEngagement} />{" "}
            <View
              margin="0 0 0 l"
              style={{
                flex: 1,
                opacity: enabledEngagement ? 1 : 0.7,
                marginBottom: -2,
              }}
            >
              <Flex align="center" direction="row">
                <Text margin="0" size="s" style={{ userSelect: "none" }}>
                  Engagement rate over
                </Text>
                <View margin="0 l 0">
                  <Dropdown
                    width={120}
                    size="xs"
                    selectionMode="single"
                    renderLabel={() => {
                      const selectedOption = engagementOptions.find(
                        (option) => option.value === Number(engagementCriteria)
                      );
                      return (
                        selectedOption?.label || engagementCriteria.toString()
                      );
                    }}
                    selectedOptions={[engagementCriteria.toString()]}
                    setSelectedOptions={(value) => {
                      if (value.length > 0) {
                        ensureCustomMode();
                        setEngagementCriteria(Number(value[0]));
                        if (!enabledEngagement) setEnabledEngagement(true);
                      }
                    }}
                    onDropdownClick={() => {
                      ensureCustomMode();
                      if (!enabledEngagement) setEnabledEngagement(true);
                    }}
                    disableOptionSort
                    options={engagementOptions
                      .sort((a, b) => a.value - b.value)
                      .map((option) => ({
                        label: option.label,
                        value: option.value.toString(),
                      }))}
                  />
                </View>
              </Flex>
            </View>
          </Flex>
          <Flex
            direction="row"
            align="center"
            margin="m 0"
            style={{ cursor: "pointer" }}
            onClick={() => {
              ensureCustomMode();
              setEnabledAudience(!enabledAudience);
            }}
          >
            <SingleCheckBox size="s" checked={enabledAudience} />{" "}
            <View
              margin="0 0 0 l"
              style={{
                flex: 1,
                opacity: enabledAudience ? 1 : 0.7,
                marginBottom: -2,
              }}
            >
              <Flex align="center" direction="row">
                <Text margin="0" size="s" style={{ userSelect: "none" }}>
                  At least
                </Text>
                <View margin="0 l 0">
                  <Dropdown
                    width={120}
                    size="xs"
                    selectionMode="single"
                    renderLabel={() => {
                      const selectedOption = audienceOptions.find(
                        (option) => option.value === Number(audienceCriteria)
                      );
                      return (
                        selectedOption?.label || audienceCriteria.toString()
                      );
                    }}
                    selectedOptions={[audienceCriteria.toString()]}
                    setSelectedOptions={(value) => {
                      if (value.length > 0) {
                        ensureCustomMode();
                        setAudienceCriteria(Number(value[0]));
                        if (!enabledAudience) setEnabledAudience(true);
                      }
                    }}
                    onDropdownClick={() => {
                      ensureCustomMode();
                      if (!enabledAudience) setEnabledAudience(true);
                    }}
                    disableOptionSort
                    options={audienceOptions
                      .sort((a, b) => a.value - b.value)
                      .map((option) => ({
                        label: option.label,
                        value: option.value.toString(),
                      }))}
                  />
                </View>
                <Text margin="0 s 0 0" size="s" style={{ userSelect: "none" }}>
                  UK-based audience
                </Text>
                {props.hasTikTokOffers ? (
                  <Tooltip>
                    <Text size="s" margin="0">
                      Only applicable for Instagram,{" "}
                      <span
                        style={{
                          fontWeight: lightTheme.typography.weight.bold,
                        }}
                      >
                        TikTok influencers will not meet this criteria
                      </span>
                    </Text>
                    <Text size="xs" margin="0" colorPreset="secondary">
                      TikTok does not currently provide demographic data
                    </Text>
                  </Tooltip>
                ) : null}
              </Flex>
            </View>
          </Flex>
          {/* Error Message */}
          {props.approvalSettingsError && (
            <Text size="xs" colorPreset="error" margin="s 0 0">
              {props.approvalSettingsError}
            </Text>
          )}
        </CustomCriteriaWrap>
      ) : null}
    </View>
  );
};

const CustomCriteriaWrap = styled.div`
  margin-left: ${(p) => p.theme.spacing.xxl};

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    margin-left: 0;
  }
`;

const ApprovalTypeSelect = (props: {
  approvalType: ApprovalType;
  selectedApprovalType: ApprovalType;
  onClick: () => void;
}) => {
  let title = "Approve All";
  let subtitle = "All influencers will be automatically approved";
  switch (props.approvalType) {
    case ApprovalType.ApprovalTypeAuto:
      title = "Approve All";
      subtitle = "All influencers will be automatically approved";
      break;
    case ApprovalType.ApprovalTypeAutoWithCriteria:
      title = "Smart Approval";
      subtitle = "Set auto-approval criteria, manually approve the rest";
      break;
    case ApprovalType.ApprovalTypeManual:
      title = "Manual Approval";
      subtitle = "Every influencer must be approved individually";
      break;
    default:
      title = "Unknown";
  }

  const showGetMoreApplications =
    props.approvalType === ApprovalType.ApprovalTypeAutoWithCriteria;

  return (
    <div style={{ position: "relative" }}>
      <AbsoluteWrap
        style={{
          opacity: showGetMoreApplications ? 1 : 0,
        }}
      >
        <MostPopular>Get 3.2x more applications</MostPopular>
      </AbsoluteWrap>
      <ApprovalTypeWrap
        onClick={props.onClick}
        selected={props.approvalType === props.selectedApprovalType}
      >
        <Flex align="center" direction="row" justify="space-between">
          <Flex align="center" direction="row" margin="0 s 0 0">
            <Flex align="center" justify="center">
              <Radio
                width={10}
                height={10}
                active={props.approvalType === props.selectedApprovalType}
              />
            </Flex>
            <View margin="0 0 0 m">
              <Text margin="0" weight="bold">
                {title}
              </Text>
              <Text margin="0" size="s" colorPreset="secondary">
                {subtitle}
              </Text>
            </View>
          </Flex>
        </Flex>
      </ApprovalTypeWrap>
    </div>
  );
};

const ApprovalTypeWrap = styled.div<{ selected: boolean }>`
  box-sizing: border-box;
  border: 1px solid ${(p) => p.theme.color.typography.secondary}50;
  border-radius: ${(p) => p.theme.misc.borderRadiusSmall};
  padding: ${(p) => p.theme.spacing.l};
  cursor: pointer;
  ${(p) =>
    p.selected
      ? css`
          border-color: ${p.theme.color.primary};
          box-shadow: inset 0 0 0 1px ${p.theme.color.primary};
        `
      : null}

  :hover {
    border-color: ${(p) => p.theme.color.typography.text};
  }
`;

const ApprovalTypeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${(p) => p.theme.spacing.m};

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    grid-template-columns: repeat(1, 1fr);
    gap: ${(p) => p.theme.spacing.l};
  }
`;

const AbsoluteWrap = styled.div`
  position: absolute;
  top: -9px;
  left: 0px;
  right: 0px;
  display: flex;
  justify-content: center;
`;

const MostPopular = styled.span`
  background-color: ${(p) => p.theme.color.primary};
  padding: 2px ${(p) => p.theme.spacing.m};
  color: #fff;
  font-size: ${(p) => p.theme.typography.size.xxxs};
  font-weight: ${(p) => p.theme.typography.weight.semi};
  border-radius: ${(p) => p.theme.misc.borderRadiusSmall};
`;

const TopCreatorsWrap = styled.div`
  background-color: ${(p) => p.theme.color.card.callout};
  padding: ${(p) => p.theme.spacing.l};
  border-radius: ${(p) => p.theme.misc.borderRadius};
  margin-top: ${(p) => p.theme.spacing.l};
`;
